import React, { useState } from "react";
import { TextField, Button, Box, Typography, Snackbar, Alert } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { IMaskInput } from "react-imask";

// Маска телефона как InputComponent
const PhoneMask = React.forwardRef(function PhoneMask(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="+1 (000) 000-0000"
      inputRef={ref}
      overwrite
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
    />
  );
});

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [loading, setLoading] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true); // ⏳ старт загрузки
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          setSnackbarMessage("Message sent successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setFormData({
            name: '',
            phone: '',
            email: '',
            company: '',
            message: '',
          });
        } else {
          setSnackbarMessage("Failed to send message.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error sending message:", error);
        setSnackbarMessage("Server error. Please try again later.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const inputStyles = {
    input: {
      borderBottom: "0.5px solid rgba(255, 255, 255, 0.75)",
      color: "#FFFFFF",
      backgroundColor: "transparent",
    },
    "& .MuiInputBase-input": {
      color: "rgba(255, 255, 255, 0.75)",
    },
    "& .MuiInputBase-inputMultiline": {
      backgroundColor: "transparent",
      borderRadius: 2,
      padding: "10px",
      color: "rgba(255, 255, 255, 0.75)",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "rgba(255, 255, 255, 0.75)",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "rgba(255, 255, 255, 0.75)",
    },
    "& .MuiInputBase-root": {
      color: "rgba(255, 255, 255, 0.75)",
      fontSize: { xs: "17px", sm: "21px" },
      fontFamily: "Outfit",
    },
    "& label": {
      color: "rgba(255, 255, 255, 0.75)",
      fontSize: { xs: "17px", sm: "21px" },
      fontFamily: "Outfit",
      fontWeight: 400,
    },
    "& label.Mui-focused": {
      color: "rgba(255, 255, 255, 0.75)",
    },
    p: "5px 0",
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: "100%", maxWidth: "650px", mx: "auto" }}
    >
      <Typography
        sx={{
          fontFamily: "Outfit",
          fontSize: "32px",
          fontWeight: 600,
          color: "#fff",
          mb: 4,
        }}
      >
        Contact Us
      </Typography>
      <TextField
        label="Name*"
        name="name"
        value={formData.name}
        onChange={handleChange("name")}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        margin="normal"
        variant="standard"
        sx={inputStyles}
      />
      <TextField
        label="Phone Number"
        name="phone"
        value={formData.phone}
        onChange={handleChange("phone")}
        InputProps={{
          inputComponent: PhoneMask,
        }}
        fullWidth
        margin="normal"
        variant="standard"
        sx={inputStyles}
      />
      <TextField
        label="Email Address*"
        name="email"
        value={formData.email}
        onChange={handleChange("email")}
        error={!!errors.email}
        helperText={errors.email}
        fullWidth
        margin="normal"
        variant="standard"
        sx={inputStyles}
      />
      <TextField
        label="Company"
        name="company"
        value={formData.company}
        onChange={handleChange("company")}
        fullWidth
        margin="normal"
        variant="standard"
        sx={inputStyles}
      />
      <Typography
        sx={{
          mt: 3,
          color: "rgba(255, 255, 255, 0.75)",
          fontFamily: "Outfit",
          fontSize: { xs: "17px", sm: "21px" },
          fontWeight: 400,
          mb: "-7px",
        }}
      >
        Message
      </Typography>
      <TextField
        name="message"
        value={formData.message}
        onChange={handleChange("message")}
        fullWidth
        margin="normal"
        multiline
        minRows={5}
        maxRows={5}
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            color: "rgba(255, 255, 255, 0.75)",
            fontFamily: "Outfit",
            borderRadius: 2,
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.75)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(255, 255, 255, 0.9)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "rgba(255, 255, 255, 0.75)",
            },
          },
          "& .MuiInputLabel-root": {
            color: "rgba(255, 255, 255, 0.75)",
            fontSize: { xs: "17px", sm: "21px" },
            fontFamily: "Outfit",
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: "rgba(255, 255, 255, 0.75)",
          },
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{
            mt: 3,
            backgroundColor: "#FFFFFF",
            color: "#000000",
            fontFamily: "Outfit",
            textTransform: "none",
            width: "160px",
            fontSize: "17px",
            fontWeight: 700,
            borderRadius: "8px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&.Mui-disabled": {
              backgroundColor: "#FFFFFF",
              color: "#000000",
              opacity: 0.6,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#000000" }} />
          ) : (
            "Send"
          )}
        </Button>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            backgroundColor: snackbarSeverity === 'success' ? '#d0f5d6' : undefined,
            color: '#1a3e1a',
            fontFamily: 'Outfit',
            fontSize: '16px',
            borderRadius: '8px',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
